// src/modules/app-views/_apps/appmulti/plans/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Badge, Button, Card, Row, Col, Checkbox, Flex, Switch, Form, Upload, theme } from 'antd';
import { FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import PricesTableModal from './modals/PricesTableModal';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import translateX from 'utils/translateX';
import plansService from 'modules/app-views/_apps/appmulti/plans/service';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import attachmentsService from 'services/attachmentsService';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const STYLES = {
  container: { position: 'relative', minHeight: 120 },
  cardMargin: { marginBottom: 16 },
  cardExtra: { display: 'flex', alignItems: 'center', gap: 8 },
  optionRow: { display: 'flex', justifyContent: 'space-between' },
  badgeDot: { marginRight: 8, marginTop: 4 },
  separator: { margin: '0 8px' },
  destinationItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 38,
    breakInside: 'avoid',
  },
  grievanceRow: { marginTop: 8 },
  planLabel: { marginLeft: 12 },
  prefixInput: { width: 90, minWidth: 90 },
  currencyInput: { width: 60, minWidth: 60 },
  tablePriceCursor: { cursor: 'pointer' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RecordTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, viewMode, refresh } = useDocumentContext();
  const { form, locked } = useSimpleFormContext();
  const isApiCreated = !!data?._integrations?.planId && viewMode !== 'add';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const showInWebsite = Form.useWatch('showInWebsite', form);
  const grievanceLevel = Form.useWatch('grievanceLevel', form) || 'none';
  // priceType managed via local state (backup parity — Form.useWatch unreliable without Form.Item)

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const isDesktop = appService.isDesktop();
  const currencyOptions = plansService.getCurrencies();
  const priceTypeOptions = plansService.getPriceTypes();
  const grievanceLevelOptions = plansService.getGrievanceLevels();
  const destinations = plansService.getDestinations();
  const travelProfiles = plansService.getTravelProfiles();
  const priceTypePrefixOptions = priceTypeOptions.map(opt => ({
    value: opt.value,
    label: translateX(opt.label),
  }));
  const currencySelectOptions = currencyOptions.map(opt => {
    const symbol = opt.label.match(/\((.+)\)/)?.[1] || opt.value;
    return { value: opt.value, label: symbol, text: opt.label };
  });
  const selectedPlanId = Form.useWatch(['_integrations', 'planId'], form);
  const destinationsGridStyle = { columnWidth: 220, columnGap: isDesktop ? 88 : 28 };
  const pdfIconStyle = { fontSize: 24, color: token.colorError };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [operatorPlans, setOperatorPlans] = useState(
    data?.__operator?._integrations?.operatorPlans || [],
  );
  const [conditionsFileUrl, setConditionsFileUrl] = useState(data?.conditionsFileUrl || null);
  const [conditionsFileName, setConditionsFileName] = useState(data?.conditionsFileName || null);
  const [conditionsUploading, setConditionsUploading] = useState(false);
  const [priceType, setPriceType] = useState(data?.priceType || 'daily');
  const [priceTableModalOpen, setPriceTableModalOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (data?.__operator?._integrations?.operatorPlans) {
      setOperatorPlans(data.__operator._integrations.operatorPlans);
    }
  }, [data?.__operator?._integrations?.operatorPlans]);

  useEffect(() => {
    const url = data?.conditionsFileUrl || null;
    const name = data?.conditionsFileName || null;
    setConditionsFileUrl(url);
    setConditionsFileName(name);
    form?.setFieldValue('conditionsFileUrl', url);
    form?.setFieldValue('conditionsFileName', name);
  }, [data?.conditionsFileUrl, data?.conditionsFileName]);

  useEffect(() => {
    const pt = data?.priceType || 'daily';
    setPriceType(pt);
    if (pt === 'table') form?.setFieldValue('price', null);
  }, [data?.priceType, data?.price]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOperatorSelect = useCallback(async entity => {
    if (!entity?._id) {
      setOperatorPlans([]);
      return;
    }
    // Fetch full entity to get _integrations.operatorPlans
    try {
      const [fullEntity] = await axiosService.get({
        url: `/v1/entities/${entity._id}`,
        returnAsObject: true,
      });
      setOperatorPlans(fullEntity?._integrations?.operatorPlans || []);
    } catch {
      setOperatorPlans([]);
    }
  }, []);

  const handleConditionsPdfUpload = file => {
    if (file.type !== 'application/pdf') {
      appService.message({ flag: 'e', text: 'file_invalid_pdf' });
      return Upload.LIST_IGNORE;
    }
    if (file.size > 3 * 1024 * 1024) {
      appService.message({ flag: 'e', text: 'file_too_large_3mb' });
      return Upload.LIST_IGNORE;
    }
    // Run async upload in IIFE — return LIST_IGNORE synchronously so antd
    // never adds the file to its internal list (avoids the blue "!" icon).
    (async () => {
      setConditionsUploading(true);
      try {
        const result = await attachmentsService.upload({ file, generateThumb: false });
        const url = result?.attachment?.storageUrl || result?.storageUrl;
        const originalName = result?.attachment?.originalName || file.name;
        if (url) {
          form?.setFieldValue('conditionsFileUrl', url);
          form?.setFieldValue('conditionsFileName', originalName);
          setConditionsFileUrl(url);
          setConditionsFileName(originalName);
          appService.message({ flag: 's', text: 'file_uploaded_successfully' });
        }
      } catch {
        appService.message({ flag: 'e', text: 'upload_error' });
      } finally {
        setConditionsUploading(false);
      }
    })();
    return Upload.LIST_IGNORE;
  };

  const handleConditionsRemove = () => {
    if (locked) return false;
    form?.setFieldValue('conditionsFileUrl', null);
    form?.setFieldValue('conditionsFileName', null);
    setConditionsFileUrl(null);
    setConditionsFileName(null);
    return true;
  };

  const handlePriceTypeChange = value => {
    const savedDaily = data?.price || 0;
    let newPrice = null;
    if (value === 'package' && savedDaily > 0) {
      newPrice = Math.round(savedDaily * 7 * 100) / 100;
    } else if (value === 'daily') {
      newPrice = savedDaily || null;
    }
    form?.setFieldValue('priceType', value);
    setPriceType(value);
    form?.setFieldValue('price', newPrice);
  };

  const handleCurrencyChange = value => {
    form?.setFieldValue('currency', value);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const operatorApiPlanOptions = buildOperatorApiPlanOptions(operatorPlans);
  const showGrievance1 = ['level1', 'level2', 'level3'].includes(grievanceLevel);
  const showGrievance2 = ['level2', 'level3'].includes(grievanceLevel);
  const showGrievance3 = grievanceLevel === 'level3';

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={STYLES.container}>
      {/* Plan Basics */}
      <Card
        title={translateX('plan')}
        style={STYLES.cardMargin}
        extra={
          <div style={STYLES.cardExtra}>
            <span className="text-muted">{translateX('show_in_website')}</span>
            <Switch
              checked={showInWebsite}
              disabled={locked}
              onChange={val => form?.setFieldValue('showInWebsite', val)}
            />
          </div>
        }
      >
        <Row gutter={16}>
          <Col xs={24} sm={24} lg={10}>
            <SimpleInput name="name" label="name" required />
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <SimpleCurrency
              name="price"
              label="price"
              required={priceType !== 'table'}
              min={0.01}
              max={99999.99}
              decimalPlaces={2}
              prefix={priceTypePrefixOptions}
              prefixDefaultValue={data?.priceType || 'daily'}
              onPrefixChange={handlePriceTypeChange}
              currencies={currencySelectOptions}
              currencyDefaultValue={data?.currency || 'USD'}
              onCurrencyChange={handleCurrencyChange}
              showCurrencySelect={priceType !== 'table'}
              x-prefix-style={STYLES.prefixInput}
              x-currency-style={STYLES.currencyInput}
              placeholder={priceType === 'table' ? 'Clique para editar' : undefined}
              readOnly={priceType === 'table'}
              onClick={priceType === 'table' ? () => setPriceTableModalOpen(true) : undefined}
              style={priceType === 'table' ? STYLES.tablePriceCursor : undefined}
            />
          </Col>

          <Col xs={24} sm={6} lg={4}>
            <SimpleNumber name="minDays" label="min_days" required min={1} max={999} />
          </Col>

          <Col xs={24} sm={6} lg={4}>
            <SimpleNumber name="maxDays" label="max_days" required min={1} max={999} />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <SimpleSelectAsyncEntities
              name="operatorId"
              label="operator"
              disabled={isApiCreated}
              required
              entityProfiles={['operator']}
              filterFn={entity => entity._integrations?.apiEnabled === true}
              selectedEntity={data?.__operator}
              onEntitySelect={handleOperatorSelect}
            />
          </Col>
          <Col xs={24} lg={14}>
            <SimpleSelect
              name={['_integrations', 'planId']}
              label="operator_api_plan"
              disabled={isApiCreated}
              options={operatorApiPlanOptions}
              allowClear
              showSearch
              useCustomLabel
              filterOption={(input, option) => {
                const text = option?.searchText || '';
                return text.toLowerCase().includes((input || '').toLowerCase());
              }}
              optionRender={option => {
                const { data } = option;
                const isActive = String(data.value) === String(selectedPlanId);
                return (
                  <div>
                    <div style={STYLES.optionRow}>
                      <span className="text-uppercase font-weight-semibold">
                        <Badge
                          dot
                          status={isActive ? 'processing' : 'default'}
                          style={STYLES.badgeDot}
                        />
                        {data.planName?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted text-uppercase">ID: {data.planIdStr}</span>
                      {data.rateCode && (
                        <>
                          <span className="text-muted" style={STYLES.separator}>
                            |
                          </span>
                          <span className="text-muted text-uppercase">
                            {translateX('rate_code')}: {data.rateCode}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Destinations */}
      <Card title={translateX('destinations')} style={STYLES.cardMargin}>
        <div style={destinationsGridStyle}>
          {destinations.map(dest => (
            <div key={dest.value} style={STYLES.destinationItem}>
              <span>{translateX(dest.label)}</span>
              <Form.Item name={['__destinations', dest.value]} valuePropName="checked" noStyle>
                <Switch disabled={locked || isApiCreated} />
              </Form.Item>
            </div>
          ))}
        </div>
      </Card>

      {/* Age Limits & Grievance */}
      <Card title={translateX('age_limits')} style={STYLES.cardMargin}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <SimpleNumber name="minAge" label="min_age" disabled={isApiCreated} required min={0} max={99} />
          </Col>
          <Col xs={24} sm={6}>
            <SimpleNumber name="maxAge" label="max_age" disabled={isApiCreated} required min={0} max={99} />
          </Col>
          <Col xs={24} sm={12}>
            <SimpleSelect
              name="grievanceLevel"
              label="grievance_level"
              disabled={isApiCreated}
              required
              options={grievanceLevelOptions}
            />
          </Col>
        </Row>

        {showGrievance1 && (
          <Row gutter={16} style={STYLES.grievanceRow}>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceMinAge1"
                label="grievance_min_age_1"
                disabled={isApiCreated}
                required
                min={0}
                max={99}
              />
            </Col>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceMaxAge1"
                label="grievance_max_age_1"
                disabled={isApiCreated}
                required
                min={0}
                max={99}
              />
            </Col>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceAdditionAge1"
                label="grievance_addition_1"
                disabled={isApiCreated}
                required
                min={1}
                max={999.99}
                decimalPlaces={2}
                percentage
              />
            </Col>
          </Row>
        )}

        {showGrievance2 && (
          <Row gutter={16} style={STYLES.grievanceRow}>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceMinAge2"
                label="grievance_min_age_2"
                disabled={isApiCreated}
                required
                min={0}
                max={99}
              />
            </Col>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceMaxAge2"
                label="grievance_max_age_2"
                disabled={isApiCreated}
                required
                min={0}
                max={99}
              />
            </Col>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceAdditionAge2"
                label="grievance_addition_2"
                disabled={isApiCreated}
                required
                min={1}
                max={999.99}
                decimalPlaces={2}
                percentage
              />
            </Col>
          </Row>
        )}

        {showGrievance3 && (
          <Row gutter={16} style={STYLES.grievanceRow}>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceMinAge3"
                label="grievance_min_age_3"
                disabled={isApiCreated}
                required
                min={0}
                max={99}
              />
            </Col>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceMaxAge3"
                label="grievance_max_age_3"
                disabled={isApiCreated}
                required
                min={0}
                max={99}
              />
            </Col>
            <Col xs={24} sm={8}>
              <SimpleNumber
                name="grievanceAdditionAge3"
                label="grievance_addition_3"
                disabled={isApiCreated}
                required
                min={1}
                max={999.99}
                decimalPlaces={2}
                percentage
              />
            </Col>
          </Row>
        )}
      </Card>

      {/* Travel Profiles */}
      <Card title={translateX('travel_profiles')} style={STYLES.cardMargin}>
        <Row gutter={[16, 8]}>
          {travelProfiles.map(profile => (
            <Col key={profile.value} xs={12} sm={8} md={6}>
              <Checkbox
                disabled={locked || isApiCreated}
                checked={form?.getFieldValue?.(['__travelProfiles', profile.value])}
                onChange={e => {
                  form?.setFieldsValue?.({
                    __travelProfiles: {
                      ...form?.getFieldValue?.('__travelProfiles'),
                      [profile.value]: e.target.checked,
                    },
                  });
                }}
              >
                {translateX(profile.label)}
              </Checkbox>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Coverage Terms PDF */}
      <Card title={<div className="text-muted">{translateX('coverage_terms')}</div>} style={STYLES.cardMargin}>
        <Upload
          accept=".pdf"
          listType="picture"
          fileList={conditionsFileUrl ? [{
            uid: '-1',
            name: conditionsFileName || conditionsFileUrl.split('?')[0].split('/').pop() || 'conditions.pdf',
            status: 'done',
            url: conditionsFileUrl,
          }] : []}
          maxCount={1}
          beforeUpload={handleConditionsPdfUpload}
          onRemove={handleConditionsRemove}
          onPreview={() => conditionsFileUrl && window.open(conditionsFileUrl, '_blank')}
          isImageUrl={() => false}
          iconRender={() => <FilePdfOutlined style={pdfIconStyle} />}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: !locked }}
          disabled={locked || conditionsUploading}
        >
          {!conditionsFileUrl && (
            <Button icon={<UploadOutlined />} loading={conditionsUploading} disabled={locked}>
              {translateX('upload')}
            </Button>
          )}
        </Upload>
      </Card>

      {/* Price Table Modal */}
      <PricesTableModal
        open={priceTableModalOpen}
        onClose={() => setPriceTableModalOpen(false)}
        documentId={data?._id}
        refresh={refresh}
        currency={form?.getFieldValue('currency') || data?.currency || 'USD'}
        locked={locked}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildOperatorApiPlanOptions(operatorPlans) {
  if (!Array.isArray(operatorPlans) || !operatorPlans.length) return [];

  return operatorPlans
    .filter(p => p.planId || p.planoId)
    .map(p => {
      const id = p.planId || p.planoId;
      const idStr = String(id);
      const name = p.planName || p.planoNome || '';
      const rateCode = p.rateCode || p.planoCodTar || '';

      return {
        value: id,
        searchText: `${name} ${idStr} ${rateCode}`,
        planName: name,
        planIdStr: idStr,
        rateCode,
        // label is used for the SELECTED display (compact single line)
        label: (
          <Flex justify="space-between" align="center">
            <span>
              <Badge
                dot
                status="processing"
                style={STYLES.badgeDot}
              />
              <span className="text-uppercase font-weight-bold">
                {name.toUpperCase()}
              </span>
            </span>
            <span className="text-muted text-uppercase" style={STYLES.planLabel}>
              ID: {idStr}
              {rateCode
                ? ` | ${translateX('rate_code')}: ${rateCode}`
                : ''}
            </span>
          </Flex>
        ),
      };
    })
    .sort((a, b) => a.searchText.localeCompare(b.searchText));
}
